import { useState, useEffect, useCallback } from 'react'
import { apiUrl, authFetch } from '../../lib/api'

interface Theme {
  id: string; name: string; slug: string; description: string
  colors: string; installs: number; is_official: number; author_id: string | null
}

export function ThemeMarketplace() {
  const [themes, setThemes] = useState<Theme[]>([])
  const [_preview, setPreview] = useState<string | null>(null)
  const [showPublish, setShowPublish] = useState(false)

  const load = useCallback(() => {
    fetch(apiUrl('/api/themes')).then(r => r.json()).then(d => setThemes(Array.isArray(d) ? d : [])).catch(() => {})
  }, [])

  useEffect(() => { load() }, [load])

  const install = (theme: Theme) => {
    try {
      const colors = typeof theme.colors === 'string' ? JSON.parse(theme.colors) : theme.colors
      let styleEl = document.getElementById('pulse-custom-theme-style')
      if (!styleEl) {
        styleEl = document.createElement('style')
        styleEl.id = 'pulse-custom-theme-style'
        document.head.appendChild(styleEl)
      }
      styleEl.textContent = `:root, [data-theme='dark'], [data-theme='light'] {
        ${colors.bg ? `--bg-0: ${colors.bg} !important;` : ''}
        ${colors.bg1 ? `--bg-1: ${colors.bg1} !important;` : ''}
        ${colors.bg2 ? `--bg-2: ${colors.bg2} !important;` : ''}
        ${colors.bg3 ? `--bg-3: ${colors.bg3} !important;` : ''}
        ${colors.bg4 ? `--bg-4: ${colors.bg4} !important;` : ''}
        ${colors.accent ? `--accent: ${colors.accent} !important;` : ''}
        ${colors.accent2 ? `--accent-2: ${colors.accent2} !important;` : ''}
        ${colors.accent3 ? `--accent-3: ${colors.accent3} !important;` : ''}
        ${colors.text ? `--text-0: ${colors.text} !important;` : ''}
        ${colors.text1 ? `--text-1: ${colors.text1} !important;` : ''}
        ${colors.text2 ? `--text-2: ${colors.text2} !important;` : ''}
        ${colors.red ? `--red: ${colors.red} !important;` : ''}
        ${colors.border ? `--border: ${colors.border} !important;` : ''}
        ${colors.border2 ? `--border-2: ${colors.border2} !important;` : ''}
      }
      /* Override background gradient with theme bg */
      .app-bg-gradient { display: none !important; }
      .cursor-spot { display: none !important; }
      .pulse-root { background: ${colors.bg || colors.bg1 || '#1a1a2e'} !important; }
      .ai-panel { background: ${colors.bg1 || colors.bg || '#16213e'} !important; }
      `
      localStorage.setItem('pulse-custom-theme', JSON.stringify(colors))
      localStorage.setItem('pulse-theme-slug', theme.slug)
      fetch(apiUrl(`/api/themes/${theme.slug}/install`), { method: 'POST' }).catch(() => {})
    } catch {}
  }

  const resetTheme = () => {
    const styleEl = document.getElementById('pulse-custom-theme-style')
    if (styleEl) styleEl.remove()
    localStorage.removeItem('pulse-custom-theme')
    localStorage.removeItem('pulse-theme-slug')
  }

  const activeSlug = localStorage.getItem('pulse-theme-slug')

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <span style={{ fontSize: 16 }}>🎨</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--text-0)' }}>Theme Marketplace</span>
        <div style={{ flex: 1 }} />
        <button onClick={resetTheme} style={btnStyle}>Reset Default</button>
        <button onClick={() => setShowPublish(p => !p)} style={{ ...btnStyle, background: 'rgba(124,110,245,0.15)', color: 'var(--accent)' }}>
          + Publish
        </button>
      </div>

      {showPublish && <PublishForm onDone={() => { setShowPublish(false); load() }} />}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
        {themes.map(t => {
          const colors = typeof t.colors === 'string' ? JSON.parse(t.colors) : t.colors
          const isActive = activeSlug === t.slug
          return (
            <div key={t.id} style={{
              background: 'var(--bg-1)', border: `1px solid ${isActive ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 10, padding: 12, cursor: 'pointer', transition: 'all 0.15s',
            }}
              onMouseEnter={() => setPreview(t.slug)}
              onMouseLeave={() => setPreview(null)}
            >
              {/* Color preview */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                {Object.values(colors).slice(0, 6).map((c, i) => (
                  <div key={i} style={{ width: 20, height: 20, borderRadius: 4, background: c as string, border: '1px solid rgba(255,255,255,0.1)' }} />
                ))}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)', marginBottom: 2 }}>
                {t.name}
                {t.is_official ? <span style={{ marginLeft: 6, fontSize: 8, color: 'var(--accent-2)', background: 'rgba(95,212,164,0.12)', padding: '1px 5px', borderRadius: 6 }}>OFFICIAL</span> : null}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-2)', marginBottom: 8, fontFamily: 'var(--font-mono)' }}>{t.description}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>⬇ {t.installs}</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => install(t)} style={{
                  ...btnStyle,
                  background: isActive ? 'rgba(95,212,164,0.15)' : 'rgba(124,110,245,0.12)',
                  color: isActive ? 'var(--accent-2)' : 'var(--accent)',
                }}>
                  {isActive ? '✓ Active' : 'Install'}
                </button>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function PublishForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [desc, setDesc] = useState('')
  const [colors, setColors] = useState('{"bg":"#1a1a2e","bg1":"#16213e","accent":"#e94560","accent2":"#0f3460","text":"#eee","red":"#ff6b6b"}')

  const submit = async () => {
    if (!name || !slug) return
    await authFetch(apiUrl('/api/themes'), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, slug, description: desc, colors }),
    })
    onDone()
  }

  return (
    <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
      <div style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-0)', marginBottom: 10 }}>Publish a Theme</div>
      <input placeholder="Theme name" value={name} onChange={e => { setName(e.target.value); setSlug(e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')) }} style={inputStyle} />
      <input placeholder="Slug" value={slug} onChange={e => setSlug(e.target.value)} style={inputStyle} />
      <input placeholder="Description" value={desc} onChange={e => setDesc(e.target.value)} style={inputStyle} />
      <textarea placeholder='{"bg":"#...","accent":"#..."}' value={colors} onChange={e => setColors(e.target.value)} style={{ ...inputStyle, height: 60, resize: 'vertical' }} />
      <button onClick={submit} style={{ ...btnStyle, background: 'rgba(124,110,245,0.2)', color: 'var(--accent)' }}>Publish</button>
    </div>
  )
}

const btnStyle: React.CSSProperties = {
  padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)',
  background: 'var(--bg-2)', color: 'var(--text-1)', cursor: 'pointer',
  fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 600, transition: 'all 0.12s',
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 10px', marginBottom: 6, borderRadius: 6,
  border: '1px solid var(--border)', background: 'var(--bg-0)', color: 'var(--text-0)',
  fontSize: 11, fontFamily: 'var(--font-mono)', outline: 'none',
}

